import { createSelector } from 'reselect';
import sortBy from 'lodash/sortBy';
import {
  Contribution,
  ContributionClassesAndRolesDDL,
  ContributionClassName,
  ContributionClassRolesDDL,
  ContributionsGroupedAndSortedList,
  ContributionsGroupedList,
  ContributionsSortedList,
} from 'types/contribution';
import { Audio, AudioTypesDDL, AudioStatusType } from 'types/audio';
import { SongStatusType } from 'types/song';
import {
  AudioGroupedList,
  AudioListGroupedDictionary,
} from 'app/components/Song/Audio/AudioList/types';
import { SongFormData } from 'app/components/Song/SongForm/types';
import { PerformerRoleType } from 'app/components/Song/Contributions/ContributionForm/types';
import { AppState } from 'app/store';
import { Link } from 'types/api.types';

export const isFetchingSongs = (state: AppState) =>
  state.songList.isFetchingSongs;

export const getSongListSelector = (state: AppState) => state.songList.songs;

export const getSongListLinkSelector = (state: AppState) =>
  state.auth.services?.songs?._links.listSongs;

export const getSongCountLinkSelector = (state: AppState) =>
  state.auth.services?.songs?._links.getSongCount;

export const getSongCountSelector = (state: AppState) => state.songList.count;

export const getLanguageCodesDDLinkSelector = (state: AppState) =>
  state.auth.services?.songs?._links.languageCodesAlpha2DDL;

export const getSongDDLinkSelector = (state: AppState) => {
  const {
    songKeySignaturesDDL,
    songTimeSignaturesDDL,
    songGenresDDL,
    songStatusesDDL,
  } = state.auth.services?.songs?._links || {};
  return (
    songKeySignaturesDDL ||
    songTimeSignaturesDDL ||
    songGenresDDL ||
    songStatusesDDL
  );
};

export const getSongDDLSelector = (state: AppState) => state.songList.songDDL;

export const getLanguageCodesSelector = (state: AppState) =>
  state.songList.languageCodes;

export const getCreateSongLinkSelector = (state: AppState) =>
  state.songList._links?.create;

export const getSongUpdateLinkSelector = (state: AppState) =>
  state.songList.song?._links?.updateSong;

export const getContributionUpdateLinkSelector = (state: AppState) =>
  state.songList.song?._links?.updateContribution;

export const getFetchSongLinkSelector = (state: AppState, songGuid: string) =>
  state.auth.services?.songs?._links.getSong?.href?.replace(
    '{songGuid}',
    songGuid
  ) || state.songList._links?.song.href.replace('{guid}', songGuid);

export const getSongListFromIdSelector = (state: AppState) =>
  state.songList.fromId;

export const getSong = (state: AppState) => state.songList.song;

export const getSongCollaborators = (state: AppState) =>
  state.songList.song?.collaborators;

export const getSongGuidSelector = (state: AppState) =>
  state.songList.song?.guid;

export const createSongLoadingStateSelector = (state: AppState) =>
  state.songList.createSongLoadingState;

export const getCreateContributionLinkSelector = (state: AppState) =>
  state.songList.song?._links?.createContribution;

export const getContributionsListLink = (state: AppState) =>
  state.songList.song?._links?.listContributions;

export const getIsContributionsListUpdating = (state: AppState) =>
  state.songList.updatingContributions;

export const getContributionHref = (state: AppState) =>
  state.songList.song?._links?.getContribution.href;

export const getDeleteContributionLink = (state: AppState) =>
  state.songList.song?._links?.deleteContribution;

export const getContributionsListLoadingState = (state: AppState) =>
  state.songList.contributionsLoadingState;

export const getContributionsList = (state: AppState) =>
  state.songList.song?.contributions?.items;

export const getCreateAudioHref = (state: AppState) =>
  state.songList.song?._links?.createAudio.href;

export const getAudioStatusPollingInterval = (state: AppState) =>
  state.songList.song?._links?.getAudioStatus.pollingInterval;

export const getDownloadAudioHref = (state: AppState) =>
  state.songList.song?._links?.downloadAudio.href;

export const getLocalAudioByGuid = (
  state: AppState,
  guid: string,
  parentGuid: string
) =>
  state.songList.localAudio?.[parentGuid]?.find(
    (audio) => audio.guid === guid
  ) || null;

export const getDownloadAudioLink = (state: AppState) =>
  state.songList.song?._links?.downloadAudio;

export const getListAudioLink = (state: AppState) =>
  state.songList.song?._links?.listAudio;

export const getSongListNextLink = (state: AppState) =>
  state.songList._links?.next;

export const getFolderSongListNextLink = (state: AppState) =>
  state.songList.folder?.song?._links?.next;

export const getFetchAudioLink = (state: AppState) =>
  state.songList.song?._links?.getAudio;

export const getCreditsListLink = (state: AppState) =>
  state.songList.song?._links?.listCredits;

export const getUpdateAudioLink = (state: AppState) =>
  state.songList.song?._links?.updateAudio;

export const getSongAudioListReadyForUpload = (state: AppState) =>
  state.songList.audioListReadyForUpload;

export const getSongAudioList = (state: AppState) => state.songList.song?.audio;

export const getProcessingSongAudioList = createSelector(
  [getSongAudioList],
  (audios) =>
    (audios || []).filter(
      (audio) =>
        ![AudioStatusType.SUCCESS, AudioStatusType.FAILURE].includes(
          audio.status
        )
    )
);

export const getSongAudioByGuid = (state: AppState, guid: string) =>
  state.songList.song?.audio?.find((audio) => audio.guid === guid);

export const getFetchSongAudioTypesLink = (state: AppState) =>
  state.auth.services?.songs?._links.songAudioTypesDDL;

export const getSongAudioValidations = (state: AppState) =>
  state.songList.songAudioValidations;

export const getSongAudioTypes = (state: AppState) =>
  state.songList.songAudioTypes;

export const getDeleteSongAudioHref = (state: AppState) =>
  state.songList.song?._links?.deleteAudio.href;

export const getSongValidationsSelector = (state: AppState) =>
  state.songList.validations;

export const getSongTitle = (state: AppState) => state.songList.song?.title;

export const getSongStatus = (state: AppState) => state.songList.song?.status;

export const getContributionClassesAndRolesDDL = (state: AppState) =>
  state.songList.contributionClassesAndRolesDDL;

const populateContributionClassesAndRoles = (
  classesAndRolesDDL?: ContributionClassesAndRolesDDL
) => {
  if (classesAndRolesDDL) {
    const performanceInstruments = Object.values(
      classesAndRolesDDL?.performanceInstruments || {}
    ).reduce((acc, curr) => {
      if (curr.notSelectable) {
        return {
          ...acc,
          [curr.key]: { ...curr, parent: PerformerRoleType.MUSICIAN },
        };
      }
      return { ...acc, [curr.key]: curr };
    }, {});

    return {
      ...classesAndRolesDDL,
      classRoles: {
        ...classesAndRolesDDL?.classRoles,
        ...performanceInstruments,
      },
    };
  }
  return undefined;
};

export const getContributionClassesAndRoles = createSelector(
  [getContributionClassesAndRolesDDL],
  populateContributionClassesAndRoles
);

export const getContributionClassNames = (state: AppState) =>
  state.songList.contributionClassesAndRolesDDL?.classes;

export const getContributionClassRoles = (state: AppState) =>
  state.songList.contributionClassesAndRolesDDL?.classRoles;

export const getPopulatedContributionRoles = (
  classRoles: ContributionClassRolesDDL,
  classesAndRolesDDL?: ContributionClassesAndRolesDDL
) => {
  const classesAndRoles = populateContributionClassesAndRoles(
    classesAndRolesDDL
  );

  return {
    ...classRoles,
    ...classesAndRoles?.performanceInstruments,
  };
};

const groupByClassName = (contributions: Contribution[]) => {
  const groupedContributions = contributions?.reduce<ContributionsGroupedList>(
    (acc, val) => {
      (acc[val.className] = acc[val.className] || []).push(val);
      return acc;
    },
    {}
  );
  return Object.entries(groupedContributions || {}).map(([group, items]) => ({
    className: group as ContributionClassName,
    items,
  }));
};

const groupByProfile = (
  sortedContributions: ContributionsSortedList
): ContributionsGroupedAndSortedList =>
  sortedContributions.map((value) => {
    const { items, className } = value;
    const groupedItems: any = {};
    Object.entries(items).forEach(([itemKey, item]) => {
      const { name, parent, profile, user, ...contribution } = item;
      if (profile?.guid) {
        const contributions = sortBy(
          [...(groupedItems[profile.guid]?.contributions || []), contribution],
          (entry: typeof contribution) => entry.created.timestamp
        );
        groupedItems[profile.guid] = {
          ...groupedItems[profile.guid],
          name,
          parent,
          profile,
          user,
          guid: profile.guid,
          contributions,
        };
      } else {
        groupedItems[itemKey] = {
          name,
          parent,
          profile,
          user,
          guid: contribution.guid,
          contributions: [contribution],
        };
      }
    });
    return { className, items: groupedItems };
  });

export const groupAndSortContributions = (
  contributions?: Contribution[],
  contributionClassNames?: ContributionClassRolesDDL
) => {
  if (!contributions || !contributionClassNames) return [];
  const groupsList = groupByClassName(contributions);
  const groupedSortOrder = Object.values(contributionClassNames).map(
    (className) => className.key
  );
  const sortedList = sortBy(groupsList, (entry) =>
    groupedSortOrder.indexOf(entry.className)
  );

  return groupByProfile(sortedList);
};

export const getSortedContributions = createSelector(
  [getContributionsList, getContributionClassNames],
  groupAndSortContributions
);

export const groupAudioList = (
  audioList?: Audio[],
  audioTypes?: AudioTypesDDL
): AudioGroupedList => {
  const groupedAudio = audioList?.reduce<AudioListGroupedDictionary>(
    (acc, val) => {
      (acc[val.type] = acc[val.type] || []).push(val);
      return acc;
    },
    {}
  );

  const audioListWithTypes = Object.entries(groupedAudio || {}).map(
    ([group, items]) => ({
      type: group,
      items,
    })
  );
  if (!audioTypes) {
    return audioListWithTypes;
  }

  return sortBy(audioListWithTypes, (entry) =>
    Object.keys(audioTypes).indexOf(entry.type)
  );
};

export const getGroupedAudioList = createSelector(
  [getSongAudioList, getSongAudioTypes],
  groupAudioList
);

const formatSongFormData = (
  title = '',
  status = SongStatusType.IN_CREATION
): SongFormData => ({ title, status });

export const getSongFormData = createSelector(
  [getSongTitle, getSongStatus],
  formatSongFormData
);

export const getCreateContributionValidation = (state: AppState) =>
  state.songList.createContributionValidation;

export const getInviteCollaboratorLink = (state: AppState) =>
  state.songList.song?._links?.inviteCollaborator;

export const getCountryDDLLink = (state: AppState) =>
  state.auth.services?.profiles?._links.countryCodesAlpha2DDL?.href;

export const getContributionCountryDDLLink = (state: AppState) =>
  state.auth.services?.contributions?._links.countryCodesAlpha2DDL?.href;

export const getLyricSheet = (state: AppState) =>
  state.songList.song?.lyricSheet;

export const getContributionCheckInCodeLink = (state: AppState) =>
  state.songList.song?._links?.getMyContributionCheckInCode;

export const createContributionCheckInCodeLink = (state: AppState) =>
  state.songList.song?._links?.createContributionCheckInCode;

export const getPutLatestAudioVersionLink = (state: AppState) =>
  state.songList.song?._links?.putLatestVersion;

export const getRemoveLatestAudioVersionLink = (state: AppState) =>
  state.songList.song?._links?.deleteLatestVersion;

export const getPossibleSplitMembersList = (state: AppState) =>
  state.songList?.possibleSplitMembers;

export const getContributionsForWorkSplitLink = (state: AppState) =>
  state.songList.song?._links?.listContributionsForWorkSplit;

export const getCreateSplitLink = (state: AppState) =>
  state.songList.song?._links?.createSplit;

export const getLatestWorkSplitLink = (state: AppState) =>
  state.songList.song?._links?.getLatestWorkSplit;

export const getCanViewLatestSplit = (state: AppState) =>
  Boolean(getLatestWorkSplitLink(state));

export const getShowSplits = (state: AppState) =>
  Boolean(getCreateSplitLink(state) || getLatestWorkSplitLink(state));

export const getLatestSplit = (state: AppState) => state.songList?.latestSplit;

export const getAcceptWorkSplitLink = (state: AppState) =>
  state.songList?.latestSplit?._links?.acceptWorkSplit;

export const getAffectsSplit = (role?: ContributionClassName) => (
  state: AppState
) => role === ContributionClassName.SONGWRITER && getCanViewLatestSplit(state);

export const getIsSettingPrivateAudio = (state: AppState) =>
  state.songList.isSettingPrivateAudio;

export const getIsFetchingAudio = (state: AppState) =>
  state.songList.isFetchingAudio;

export const getFetchSongsWithAudioListsLink = (state: AppState) =>
  state.auth.services?.songs?._links.listSongAudio;

export const getDownloadSplitPdfLink = (state: AppState) =>
  state.songList.latestSplit?._links?.getLatestWorkSplitPdf;

export const getLyricSheetPdfDonwloadLink = (state: AppState) =>
  state.songList.song?.lyricSheet?._links?.getLyricSheetPdf;

export const getSetLyricsSheetVersionLink = (state: AppState) =>
  state.songList.song?._links?.setLyricSheetVersion;

export const getLyricVersion = (state: AppState) =>
  state.songList.song?.lyricsVersion?.version;

export const getLyricsListLink = (state: AppState) =>
  state.songList.song?._links?.listLyricSheets;

export const getSongListValidation = (state: AppState) =>
  state.songList.songListValidations;

export const getSongAudioListValidation = (state: AppState) =>
  state.songList.songAudioListValidations;

export const getSongUsualSuspects = (state: AppState) =>
  state.songList?.song?.usualSuspects || [];

export const getSongsFolders = (state: AppState) =>
  state.songList.folder?.items || [];

export const getCreateFolderLink = (state: AppState) =>
  state.songList.folder?._links?.createFolder;

export const getDeleteFolderLink = (guid: string) => (
  state: AppState
): Link => ({
  href: `${state.auth.services?.songs?._links.listSongFolders?.href}/${guid}`,
  method: 'DELETE',
});

export const getUpdateFolderLink = (guid: string) => (
  state: AppState
): Link => ({
  href: `${state.auth.services?.songs?._links.listSongFolders?.href}/${guid}`,
  method: 'PATCH',
});

export const getFolder = (guid: string) => (state: AppState) =>
  state.songList.folder?.items?.find((folder) => folder.guid === guid);

export const getFolderSongs = (state: AppState) =>
  state.songList.folder?.song?.items || [];

export const getFoldersNextLink = (state: AppState) =>
  state.songList.folder?._links?.next;

export const getPinnedSongs = (state: AppState) =>
  state.songList.pinned?.songs || [];

export const getImportRecordLink = (state: AppState) =>
  state.songList.song?._links?.getImportRecord;
